import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, Upload as UploadIcon, MessageCircle, ClipboardList, Users, Calendar,
  Film, CheckCircle2, Send, Rocket, ChevronDown, Trash2, Download, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccGet, mccPost, mccPatch, mccDelete, mccDownloadUrl } from './mccApi';
import {
  PLATFORMS, PROJECT_STAGES, CONTENT_TYPES, PRIORITIES, statusTone, humanDateTime, humanDate,
} from './mccConstants';
import {
  StatusPill, PriorityPill, PlatformIcon, Avatar, AvatarStack, DueBadge, InfoRow,
} from './mccUi';
import MccUploader from './MccUploader';
import MccVideoReview from './MccVideoReview';

const TABS = [
  { v: 'brief', l: 'Brief', icon: ClipboardList },
  { v: 'files', l: 'Files', icon: Film },
  { v: 'versions', l: 'Versions & Feedback', icon: MessageCircle },
  { v: 'approvals', l: 'Approvals', icon: CheckCircle2 },
  { v: 'publishing', l: 'Publishing', icon: Rocket },
  { v: 'activity', l: 'Activity', icon: Users },
];

export default function MccProjectDrawer({ projectId, team, onClose, onChanged }) {
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('brief');
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [commentText, setCommentText] = useState('');

  const nameOf = useCallback((uid) => (team.find((t) => t.id === uid) || {}).name || uid || '—', [team]);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const p = await mccGet(`/marketing/projects/${projectId}`);
      const fl = await mccGet(`/marketing/uploads`, { project_id: projectId });
      setProject(p);
      setFiles(fl.items || []);
      if (!selectedVersionId && (p.versions || []).length) {
        setSelectedVersionId(p.versions[p.versions.length - 1].id);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedVersionId]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [projectId]);

  const patch = async (patchObj) => {
    if (!project) return;
    try {
      const updated = await mccPatch(`/marketing/projects/${project.id}`, patchObj);
      setProject(updated);
      onChanged?.();
      toast.success('Saved');
    } catch (e) { toast.error(e.message || 'Save failed'); }
  };

  const addComment = async () => {
    const txt = commentText.trim();
    if (!txt) return;
    try {
      const updated = await mccPost(`/marketing/projects/${project.id}/comments`, { comment: txt });
      setProject(updated);
      setCommentText('');
    } catch (e) { toast.error(e.message || 'Comment failed'); }
  };

  const approve = async (decision) => {
    if (!selectedVersionId) { toast.error('Select a version first'); return; }
    const feedback = decision === 'changes_required' ? prompt('What changes are required?') : null;
    try {
      const res = await mccPost('/marketing/versions/approve', {
        version_id: selectedVersionId, decision, feedback,
      });
      toast.success(`Version ${decision.replace(/_/g, ' ')}`);
      await refresh();
      onChanged?.();
      return res;
    } catch (e) { toast.error(e.message || 'Approve failed'); }
  };

  const scheduleOrPublish = async ({ platform, scheduled_at, published_at, published_link, caption, title, description }) => {
    try {
      await mccPost('/marketing/publishing/schedule', {
        project_id: project.id, platform,
        scheduled_at, published_at, published_link, caption, title, description,
      });
      await refresh();
      onChanged?.();
      toast.success('Publishing details saved');
    } catch (e) { toast.error(e.message || 'Publish failed'); }
  };

  const removeFile = async (fid) => {
    if (!window.confirm('Delete this file?')) return;
    try { await mccDelete(`/marketing/uploads/${fid}`); await refresh(); toast.success('File removed'); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const selectedVersion = useMemo(
    () => (project?.versions || []).find((v) => v.id === selectedVersionId) || null,
    [project, selectedVersionId]
  );

  if (!projectId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[1080px] h-full bg-white shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {loading || !project ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 w-40 bg-slate-200 rounded" />
                <div className="h-7 w-80 bg-slate-200 rounded" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-[10px] font-semibold text-indigo-600 uppercase tracking-[0.14em]">
                  <span>Content project</span>
                  <span>·</span>
                  <span className="font-mono text-slate-500">{project.id.slice(0, 8)}</span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mt-1 leading-tight">{project.title}</h2>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <StatusPill status={project.status} />
                  <PriorityPill priority={project.priority} />
                  <DueBadge dueIso={project.due_date} />
                  {project.primary_platform ? <PlatformIcon platform={project.primary_platform} size={22} /> : null}
                  {(project.additional_platforms || []).map((p) => <PlatformIcon key={p} platform={p} size={22} />)}
                </div>
              </>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="mcc-drawer-close"><X className="w-4 h-4" /></Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-3 overflow-x-auto flex-shrink-0">
          <div className="flex gap-1">
            {TABS.map(({ v, l, icon: Icon }) => (
              <button
                key={v}
                onClick={() => setTab(v)}
                data-testid={`mcc-drawer-tab-${v}`}
                className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition ${
                  tab === v
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50/60">
          {loading || !project ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : tab === 'brief' ? (
            <BriefTab project={project} team={team} onPatch={patch} nameOf={nameOf} />
          ) : tab === 'files' ? (
            <FilesTab project={project} files={files} onRefresh={refresh} onRemove={removeFile} />
          ) : tab === 'versions' ? (
            <VersionsTab
              project={project} team={team}
              onRefresh={refresh}
              selectedVersion={selectedVersion}
              onSelectVersion={setSelectedVersionId}
            />
          ) : tab === 'approvals' ? (
            <ApprovalsTab
              project={project} team={team}
              selectedVersion={selectedVersion}
              onSelectVersion={setSelectedVersionId}
              onApprove={approve}
            />
          ) : tab === 'publishing' ? (
            <PublishingTab project={project} team={team} onPatch={patch} onSchedule={scheduleOrPublish} />
          ) : (
            <ActivityTab
              project={project}
              onAddComment={addComment}
              commentText={commentText}
              setCommentText={setCommentText}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Brief tab ───────────────────────────────────────────
function BriefTab({ project, team, onPatch, nameOf }) {
  const [local, setLocal] = useState(project);
  useEffect(() => setLocal(project), [project.id]);

  const commit = (k, v) => { setLocal((s) => ({ ...s, [k]: v })); onPatch({ [k]: v }); };
  const toggleAssignee = (uid) => {
    const set = new Set(local.assigned_to || []);
    if (set.has(uid)) set.delete(uid); else set.add(uid);
    commit('assigned_to', Array.from(set));
  };
  const togglePlatform = (p) => {
    const set = new Set(local.additional_platforms || []);
    if (set.has(p)) set.delete(p); else set.add(p);
    commit('additional_platforms', Array.from(set));
  };

  return (
    <div className="p-5 space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        {/* Left column — creative brief */}
        <div className="space-y-4">
          <FieldTextarea label="Description" value={local.description} onSave={(v) => commit('description', v)} rows={5} />
          <FieldTextarea label="Hook" value={local.hook} onSave={(v) => commit('hook', v)} rows={2} />
          <FieldTextarea label="Script" value={local.script} onSave={(v) => commit('script', v)} rows={8} />
          <FieldTextarea label="Caption" value={local.caption} onSave={(v) => commit('caption', v)} rows={3} />
          <FieldText label="Call to action" value={local.call_to_action} onSave={(v) => commit('call_to_action', v)} />
          <FieldText label="Related campaign" value={local.related_campaign} onSave={(v) => commit('related_campaign', v)} />
        </div>
        {/* Right column — meta */}
        <div className="space-y-4">
          <SelectField label="Content type" value={local.content_type} onChange={(v) => commit('content_type', v)}
            options={CONTENT_TYPES.map((c) => ({ v: c.v, l: c.l }))} />
          <SelectField label="Primary platform" value={local.primary_platform} onChange={(v) => commit('primary_platform', v)}
            options={PLATFORMS.map((p) => ({ v: p.v, l: p.l }))} />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Additional platforms</p>
            <div className="flex flex-wrap gap-1">
              {PLATFORMS.map((p) => {
                const active = (local.additional_platforms || []).includes(p.v);
                const Icon = p.icon;
                return (
                  <button
                    key={p.v}
                    onClick={() => togglePlatform(p.v)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition ${
                      active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <Icon className="w-3 h-3" style={{ color: active ? 'inherit' : p.tone }} /> {p.l}
                  </button>
                );
              })}
            </div>
          </div>
          <SelectField label="Priority" value={local.priority} onChange={(v) => commit('priority', v)}
            options={PRIORITIES.map((p) => ({ v: p.v, l: p.l }))} />
          <SelectField label="Status" value={local.status} onChange={(v) => commit('status', v)}
            options={PROJECT_STAGES.map((s) => ({ v: s, l: (statusTone(s).label) }))} />

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Due date</p>
            <Input type="date" value={(local.due_date || '').slice(0, 10)}
              onChange={(e) => commit('due_date', e.target.value)} />
          </div>

          <FieldText label="Person in content" value={local.person_in_content} onSave={(v) => commit('person_in_content', v)} />

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Assigned team</p>
            <div className="flex flex-wrap gap-1">
              {team.map((m) => {
                const active = (local.assigned_to || []).includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => toggleAssignee(m.id)}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition ${
                      active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`mcc-brief-assign-${m.id}`}
                  >
                    <Avatar name={m.name} size={16} /> {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <RoleSelect label="Editor" value={local.editor_id} onChange={(v) => commit('editor_id', v)} team={team} />
            <RoleSelect label="Approver" value={local.approver_id} onChange={(v) => commit('approver_id', v)} team={team} />
            <RoleSelect label="Publisher" value={local.publisher_id} onChange={(v) => commit('publisher_id', v)} team={team} />
          </div>

          <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-200">
            Created by <b>{project.created_by_name}</b> · {humanDateTime(project.created_at)}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldText({ label, value, onSave }) {
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (value || '') && onSave(v)} placeholder="—" />
    </div>
  );
}

function FieldTextarea({ label, value, onSave, rows = 3 }) {
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <Textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (value || '') && onSave(v)} rows={rows} placeholder="—" />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <Select value={value || ''} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className="h-9"><SelectValue placeholder={`Select ${label.toLowerCase()}…`} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function RoleSelect({ label, value, onChange, team }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="h-9"><SelectValue placeholder={`Set ${label.toLowerCase()}…`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— None —</SelectItem>
          {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Files tab ───────────────────────────────────────────
function FilesTab({ project, files, onRefresh, onRemove }) {
  const [kind, setKind] = useState('raw_video');
  return (
    <div className="p-5 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border rounded-xl bg-white p-4">
          <p className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><UploadIcon className="w-4 h-4" /> Upload supporting files</p>
          <div className="mb-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">File kind</p>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="raw_video">Raw video</SelectItem>
                <SelectItem value="raw_audio">Raw audio</SelectItem>
                <SelectItem value="raw_image">Raw image</SelectItem>
                <SelectItem value="script">Script</SelectItem>
                <SelectItem value="brief">Brief</SelectItem>
                <SelectItem value="graphic">Graphic</SelectItem>
                <SelectItem value="thumbnail">Thumbnail</SelectItem>
                <SelectItem value="brand_asset">Brand asset</SelectItem>
                <SelectItem value="reference">Reference</SelectItem>
                <SelectItem value="document">Document</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <MccUploader
            endpoint="/marketing/uploads"
            extraFields={{ project_id: project.id, kind }}
            onUploaded={onRefresh}
            dark={false}
            compact
            label={`Drop ${kind.replace(/_/g, ' ')} here`}
            sub="Uploads store to Emergent object storage and stay linked to this project."
          />
        </div>
        <div className="border rounded-xl bg-white p-4">
          <p className="text-sm font-semibold text-slate-900 mb-3">Files in this project</p>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {files.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-6">No files yet.</p>
            ) : files.map((f) => (
              <div key={f.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 group">
                <Film className="w-4 h-4 text-indigo-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{f.original_filename}</p>
                  <p className="text-[10px] text-slate-500">
                    {(f.kind || 'file').replace(/_/g, ' ')} · {(f.size / (1024 * 1024)).toFixed(1)} MB · {f.uploaded_by_name}
                  </p>
                </div>
                <a
                  href={mccDownloadUrl(f.id)}
                  target="_blank" rel="noreferrer"
                  className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-slate-800 p-1"
                  title="Open"
                ><Download className="w-3.5 h-3.5" /></a>
                <button
                  onClick={() => onRemove(f.id)}
                  className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 p-1"
                  title="Delete"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Versions & feedback tab ─────────────────────────────
function VersionsTab({ project, onRefresh, selectedVersion, onSelectVersion }) {
  const versions = project.versions || [];
  return (
    <div className="p-5 space-y-4">
      <div className="border rounded-xl bg-slate-950 text-slate-100 p-4">
        <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-[0.14em] mb-2">Upload edited version</p>
        <div className="grid md:grid-cols-2 gap-4">
          {PLATFORMS.slice(0, 5).map((p) => (
            <UploadVersionCard key={p.v} project={project} platform={p.v} platformLabel={p.l} onRefresh={onRefresh} />
          ))}
        </div>
        <UploadVersionCard project={project} platform={null} platformLabel="Master (no specific platform)" onRefresh={onRefresh} />
      </div>

      <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Versions ({versions.length})</p>
          {versions.length === 0 ? (
            <p className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-300 rounded-lg">No versions yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
              {[...versions].reverse().map((v) => (
                <button
                  key={v.id}
                  onClick={() => onSelectVersion(v.id)}
                  className={`w-full text-left border rounded-lg overflow-hidden hover:border-indigo-400 transition ${
                    selectedVersion?.id === v.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`mcc-version-select-${v.id}`}
                >
                  {v.content_type?.startsWith('video/') && v.file_id ? (
                    <div className="bg-black aspect-video w-full">
                      <video
                        src={mccDownloadUrl(v.file_id) + '#t=0.5'}
                        preload="metadata"
                        muted
                        playsInline
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : v.content_type?.startsWith('image/') && v.file_id ? (
                    <div className="bg-slate-100 aspect-video w-full">
                      <img
                        src={mccDownloadUrl(v.file_id)}
                        alt={v.file_name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : null}
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-indigo-600">V{v.version_number}</span>
                      {v.platform ? <PlatformIcon platform={v.platform} size={16} /> : null}
                      <span className="ml-auto text-[10px] text-slate-500">{humanDate(v.uploaded_at)}</span>
                    </div>
                    <p className="text-sm font-medium mt-0.5 truncate">{v.label || v.file_name}</p>
                    <p className="text-[10px] text-slate-500 truncate mt-0.5">{v.uploaded_by_name}</p>
                    {v.approval_status && (
                      <p className="text-[10px] mt-1">
                        <StatusPill status={v.approval_status} size="sm" />
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="min-w-0">
          {selectedVersion ? (
            <MccVideoReview version={selectedVersion} onFeedback={onRefresh} />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 text-center py-16 px-4 bg-slate-50">
              <MessageCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-slate-700 font-semibold">Select a version to review</p>
              <p className="text-xs text-slate-500 mt-1">Once an editor uploads a draft here you can leave timestamped feedback.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadVersionCard({ project, platform, platformLabel, onRefresh }) {
  const [notes, setNotes] = useState('');
  const [label, setLabel] = useState('');
  return (
    <div className="rounded-lg bg-black/30 border border-slate-800 p-3 space-y-2">
      <div className="flex items-center gap-2 mb-1">
        {platform ? <PlatformIcon platform={platform} size={22} /> : <Film className="w-5 h-5 text-slate-300" />}
        <p className="text-sm font-semibold text-slate-100">{platformLabel}</p>
      </div>
      <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Version label (e.g. V1 rough cut)"
        className="bg-slate-900 border-slate-800 text-slate-100 text-sm placeholder:text-slate-500" />
      <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Editing notes (optional)"
        className="bg-slate-900 border-slate-800 text-slate-100 text-sm placeholder:text-slate-500 resize-none" />
      <MccUploader
        endpoint="/marketing/versions"
        extraFields={{ project_id: project.id, platform: platform || '', kind: 'edited_video', label, editing_notes: notes }}
        onUploaded={() => { setNotes(''); setLabel(''); onRefresh(); }}
        multiple={false}
        dark
        compact
        label={`Drop ${platformLabel} version`}
        sub="MP4, MOV, WebM — up to 2 GB"
      />
    </div>
  );
}

// ── Approvals tab ───────────────────────────────────────
function ApprovalsTab({ project, selectedVersion, onSelectVersion, onApprove }) {
  const versions = project.versions || [];
  return (
    <div className="p-5 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Versions</p>
          <div className="space-y-1 max-h-[420px] overflow-y-auto">
            {versions.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-6">No versions to approve yet.</p>
            ) : [...versions].reverse().map((v) => (
              <button
                key={v.id}
                onClick={() => onSelectVersion(v.id)}
                className={`w-full text-left border rounded-lg px-3 py-2.5 ${selectedVersion?.id === v.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-indigo-600">V{v.version_number}</span>
                  {v.platform ? <PlatformIcon platform={v.platform} size={16} /> : null}
                  <span className="ml-auto"><StatusPill status={v.approval_status || 'awaiting_approval'} size="sm" /></span>
                </div>
                <p className="text-sm font-semibold mt-1">{v.label || v.file_name}</p>
                {v.reviewed_at ? <p className="text-[10px] text-slate-500 mt-1">Reviewed by {v.reviewed_by_name} · {humanDateTime(v.reviewed_at)}</p> : null}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Decision</p>
          {!selectedVersion ? (
            <p className="text-sm text-slate-500 border border-dashed rounded-lg py-6 text-center">Pick a version.</p>
          ) : (
            <div className="space-y-2">
              <Button onClick={() => onApprove('approved')} className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mcc-approve">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Approved
              </Button>
              <Button onClick={() => onApprove('approved_ready_to_publish')} className="w-full justify-start bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mcc-approve-ready">
                <Rocket className="w-4 h-4 mr-2" /> Approved & Ready to Publish
              </Button>
              <Button onClick={() => onApprove('changes_required')} variant="outline" className="w-full justify-start border-amber-500 text-amber-800" data-testid="mcc-changes-required">
                Changes Required
              </Button>
              <Button onClick={() => onApprove('rejected')} variant="outline" className="w-full justify-start border-rose-500 text-rose-800" data-testid="mcc-reject">
                Rejected
              </Button>
              <p className="text-[11px] text-slate-500 mt-2">When you request changes, the assigned editor is notified and the task returns to their queue with the requested changes visible.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Publishing tab ──────────────────────────────────────
function PublishingTab({ project, onSchedule }) {
  const platforms = [project.primary_platform, ...(project.additional_platforms || [])].filter(Boolean);
  const uniq = Array.from(new Set(platforms));
  const publishing = project.publishing || {};

  const [drafts, setDrafts] = useState(() => {
    const out = {};
    uniq.forEach((p) => { out[p] = publishing[p] || {}; });
    return out;
  });
  const [assisting, setAssisting] = useState(null);
  useEffect(() => {
    const out = {};
    uniq.forEach((p) => { out[p] = publishing[p] || {}; });
    setDrafts(out);
    // eslint-disable-next-line
  }, [project.id]);

  if (uniq.length === 0) return <div className="p-6 text-sm text-slate-500">Set at least one platform in the Brief tab to schedule publishing.</div>;

  const save = (platform, key, value) => setDrafts((d) => ({ ...d, [platform]: { ...(d[platform] || {}), [key]: value } }));
  const doSave = (platform) => onSchedule({ platform, ...drafts[platform] });
  const markPublished = (platform) => onSchedule({ platform, ...drafts[platform], published_at: new Date().toISOString() });

  const suggestCaption = async (platform) => {
    setAssisting(platform);
    try {
      const res = await mccPost('/marketing/ai/caption', { project_id: project.id, platform, tone: 'professional' });
      save(platform, 'caption', res.caption || '');
      toast.success(`AI caption drafted for ${platform.replace(/_/g, ' ')}`);
    } catch (e) {
      toast.error(e.message || 'AI caption failed');
    } finally {
      setAssisting(null);
    }
  };

  return (
    <div className="p-5 space-y-4">
      {uniq.map((p) => {
        const d = drafts[p] || {};
        return (
          <div key={p} className="border rounded-xl bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <PlatformIcon platform={p} size={26} />
              <p className="font-semibold text-slate-900">{(PLATFORMS.find((pp) => pp.v === p) || {}).l || p}</p>
              {d.published_at ? <StatusPill status="published" size="sm" /> :
                d.scheduled_at ? <StatusPill status="scheduled_to_publish" size="sm" /> : null}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Title</label>
                <Input value={d.title || ''} onChange={(e) => save(p, 'title', e.target.value)} placeholder="Post title" />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Scheduled date & time</label>
                <Input type="datetime-local" value={(d.scheduled_at || '').slice(0, 16)} onChange={(e) => save(p, 'scheduled_at', e.target.value ? new Date(e.target.value).toISOString() : '')} />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Caption</label>
                  <button
                    onClick={() => suggestCaption(p)}
                    disabled={assisting === p}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
                    data-testid={`mcc-ai-caption-${p}`}
                  >
                    <Sparkles className="w-3 h-3" />
                    {assisting === p ? 'Drafting…' : 'Suggest with AI'}
                  </button>
                </div>
                <Textarea rows={3} value={d.caption || ''} onChange={(e) => save(p, 'caption', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Description</label>
                <Textarea rows={2} value={d.description || ''} onChange={(e) => save(p, 'description', e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Call to action</label>
                <Input value={d.call_to_action || ''} onChange={(e) => save(p, 'call_to_action', e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Published link</label>
                <Input value={d.published_link || ''} onChange={(e) => save(p, 'published_link', e.target.value)} placeholder="https://…" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => doSave(p)}>Save schedule</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => markPublished(p)}>Mark as published</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Activity tab ────────────────────────────────────────
function ActivityTab({ project, onAddComment, commentText, setCommentText }) {
  const items = [
    ...(project.activity || []).map((a) => ({ ...a, _kind: 'activity' })),
    ...(project.comments || []).map((c) => ({ ...c, _kind: 'comment' })),
  ].sort((a, b) => (a.at || a.created_at || '').localeCompare(b.at || b.created_at || ''));

  return (
    <div className="p-5">
      <div className="border rounded-xl bg-white p-4 mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Add internal comment</p>
        <Textarea rows={3} value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Type a note for the team…" />
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={onAddComment} data-testid="mcc-comment-add"><Send className="w-3 h-3 mr-1" /> Post</Button>
        </div>
      </div>

      <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
        {items.length === 0 ? (
          <p className="text-xs text-slate-500 py-6 text-center">No activity yet.</p>
        ) : items.map((it) => (
          <div key={it.id} className="border rounded-lg bg-white px-3 py-2 flex items-start gap-2">
            <Avatar name={it.by_name} size={22} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <span className="font-semibold text-slate-800">{it.by_name}</span>
                <span className="ml-auto">{humanDateTime(it.at || it.created_at)}</span>
              </div>
              <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">
                {it._kind === 'comment' ? it.comment : (
                  <span><span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider mr-1">{it.action}</span>{it.detail}</span>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
